'use client';

import { createClient } from '@/lib/supabase/client';

export type PortfolioBucket = 'logos-brand-identity' | 'content-creation' | 'websites-seo';

export interface StorageMediaItem {
  id: string;
  name: string;
  url: string;
  type: 'image' | 'video';
  bucket: PortfolioBucket;
  createdAt: string;
}

function getMediaType(name: string): 'image' | 'video' {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const videoExts = ['mp4', 'webm', 'ogg', 'mov', 'quicktime'];
  return videoExts.includes(ext) ? 'video' : 'image';
}

export async function listBucketMedia(bucket: PortfolioBucket): Promise<StorageMediaItem[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .list('', { limit: 100, offset: 0, sortBy: { column: 'created_at', order: 'desc' } });

    if (error) {
      console.log('Storage list error:', error.message);
      return [];
    }

    const files = (data || []).filter((f) => f.name !== '.emptyFolderPlaceholder');

    return files.map((file) => {
      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(file.name);
      return {
        id: file.id || file.name,
        name: file.name,
        url: urlData.publicUrl,
        type: getMediaType(file.name),
        bucket,
        createdAt: file.created_at || '',
      };
    });
  } catch (err: any) {
    console.log('Storage error:', err.message);
    return [];
  }
}

export async function uploadMediaFile(
  bucket: PortfolioBucket,
  file: File
): Promise<{ url: string; name: string } | null> {
  const supabase = createClient();
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const fileName = `${timestamp}_${safeName}`;

  try {
    const { error } = await supabase.storage.from(bucket).upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

    if (error) {
      console.log('Upload error:', error.message);
      return null;
    }

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(fileName);
    return { url: urlData.publicUrl, name: fileName };
  } catch (err: any) {
    console.log('Upload error:', err.message);
    return null;
  }
}

export async function deleteMediaFile(bucket: PortfolioBucket, fileName: string): Promise<boolean> {
  const supabase = createClient();
  try {
    const { error } = await supabase.storage.from(bucket).remove([fileName]);
    if (error) {
      console.log('Delete error:', error.message);
      return false;
    }
    return true;
  } catch (err: any) {
    console.log('Delete error:', err.message);
    return false;
  }
}
